/*
 * UserSignupForm.jsx
 * -----------------
 * DEVELOPER NOTES FOR FUTURE AI ASSISTANCE:
 * 
 * IMPORTANT DATABASE OPERATIONS:
 * 1. This component handles user signup for events, creating user accounts in:
 *    - Supabase Auth (automatic)
 *    - app_users table (requires RLS bypass)
 *    - event_orders table
 * 
 * KNOWN ISSUES & SOLUTIONS:
 * - Problem: User records created in Auth but not in app_users table
 *   Solution: Use SECURITY DEFINER functions to bypass RLS policies
 * 
 * - Problem: SQL syntax errors with the event_id field
 *   Solution: Use COALESCE() for handling event_id in SQL queries, not direct assignment
 *
 * - Problem: Multiple database approach is needed to handle edge cases
 *   Solution: Component uses a 3-step fallback approach for database operations:
 *     1. RPC function (create_event_signup_user_v2) - most reliable
 *     2. Direct database operations (insert/update)
 *     3. Simple RPC function (create_simple_user) - as final fallback
 *
 * REQUIRED SQL FUNCTIONS:
 * - create_event_signup_user_v2(user_id, user_email, user_first_name, user_last_name, user_phone, user_event_id, ...)
 * - create_simple_user(p_user_id, p_first_name, p_last_name, p_email, p_event_id)
 *
 * TABLE REQUIREMENTS:
 * - app_users table must have 'event_id' column of type UUID
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Radio,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  useToast,
  Spinner,
  Container,
  Stack,
  Divider,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormHelperText,
  InputGroup,
  InputLeftAddon,
  Progress,
  HStack,
  Flex,
  Image,
  Badge,
  Icon,
  Code,
  Tooltip,
  Collapse,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, StarIcon, InfoIcon, ViewIcon, QuestionIcon } from '@chakra-ui/icons';
import { FaChevronUp, FaChevronDown, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';

export function UserSignupForm() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [event, setEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [authStep, setAuthStep] = useState('profile');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLocationDetails, setShowLocationDetails] = useState(true);
  const [hideTooltips] = useState(true);
  
  // User profile data
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    user_type: 'customer',
    phone: '',
    team_name: '',
    jersey_number: ''
  });
  
  // Progress tracking
  const [signupProgress, setSignupProgress] = useState(0);

  useEffect(() => {
    if (eventId) {
      console.log('Component mounted, fetching event details for ID:', eventId);
      fetchEventDetails();
    } else {
      console.log('Component mounted, fetching all events');
      fetchEvents();
    }
    
    // Automatically set auth step to complete to show template fields
    setAuthStep('complete');
  }, [eventId]);

  // Monitor auth step changes
  useEffect(() => {
    console.log('Auth step changed to:', authStep);
  }, [authStep]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      console.log('User already authenticated:', session.user);
      setCurrentUser(session.user);
      
      // Check if user already has a profile in app_users
      const { data: userData, error: userError } = await supabase
        .from('app_users')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      if (userData) {
        console.log('User profile found:', userData);
        // Pre-fill profile data if available
        setProfileData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          user_type: userData.user_type || 'customer',
          phone: userData.phone || '',
          team_name: userData.team_name || '',
          jersey_number: userData.jersey_number || ''
        });
        
        // If user has completed profile, move to the form or to dashboard
        if (userData.first_name && userData.last_name) {
          setAuthStep('complete');
        } else {
          setAuthStep('profile');
        }
      } else {
        // User is authenticated but has no profile
        console.log('User has no profile yet');
        setAuthStep('profile');
      }
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'active');

      if (error) throw error;
      
      // Parse tags for each event
      const eventsWithTags = data?.map(event => {
        let parsedTags = [];
        if (event.tags) {
          try {
            parsedTags = typeof event.tags === 'string' 
              ? JSON.parse(event.tags) 
              : (Array.isArray(event.tags) ? event.tags : []);
          } catch (e) {
            console.error('Error parsing tags for event:', event.id, e);
          }
        }
        return { ...event, tags: parsedTags };
      }) || [];
      
      setEvents(eventsWithTags);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: 'Error',
        description: 'Failed to load events',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      
      console.log('Event data loaded:', data);
      console.log('Event templates:', data.templates);
      
      // Parse tags
      let parsedTags = [];
      if (data.tags) {
        try {
          parsedTags = typeof data.tags === 'string'
            ? JSON.parse(data.tags)
            : (Array.isArray(data.tags) ? data.tags : []);
        } catch (e) {
          console.error('Error parsing tags for event:', data.id, e);
        }
      }
      
      // Fetch templates to get placeholder data
      let placeholders = {};
      let templateFields = [];
      
      if (data.templates) {
        try {
          // Parse template IDs from comma-separated string
          const templateIds = data.templates.split(',').filter(id => id.trim());
          
          console.log('Template IDs extracted:', templateIds);
          
          if (templateIds.length > 0) {
            // Fetch templates from the templates table
            const { data: templatesData, error: templatesError } = await supabase
              .from('templates')
              .select('id, name, placeholder_data, scene_url')
              .in('id', templateIds);
              
            if (templatesError) throw templatesError;
            
            console.log('Templates data loaded:', templatesData);
            
            // Process each template to extract fields
            templatesData.forEach(template => {
              // Extract standard fields from placeholder_data if available
              if (template.placeholder_data) {
                console.log(`Template ${template.id} placeholder_data:`, template.placeholder_data);
                
                try {
                  let templatePlaceholders;
                  if (typeof template.placeholder_data === 'string') {
                    templatePlaceholders = JSON.parse(template.placeholder_data);
                  } else {
                    templatePlaceholders = template.placeholder_data;
                  }
                  
                  console.log(`Parsed placeholder data for ${template.name}:`, templatePlaceholders);
                  
                  // Handle array-formatted placeholder data
                  if (Array.isArray(templatePlaceholders)) {
                    templatePlaceholders.forEach(placeholder => {
                      if (placeholder.name && placeholder.type) {
                        // Skip 'Person' placeholders (image placeholders)
                        if (placeholder.name === 'Person' || 
                            placeholder.type === '//ly.img.ubq/graphic') {
                          return;
                        }
                        
                        // Map common field names
                        let fieldName = placeholder.name.toUpperCase();
                        if (fieldName === 'LAST NAME' || fieldName === 'LAST NAMER') {
                          fieldName = 'LAST NAME';
                        } else if (fieldName === 'JERSEY' || fieldName === 'NUMBER') {
                          fieldName = 'JERSEY NUMBER';
                        }
                        
                        templateFields.push({
                          name: fieldName,
                          originalName: placeholder.name,
                          type: 'text',
                          template: template.name,
                          defaultContent: placeholder.defaultContent || ''
                        });
                        
                        console.log(`Added field from array: ${fieldName}`);
                      }
                    });
                  } 
                  // Handle object-formatted placeholder data
                  else if (typeof templatePlaceholders === 'object') {
                    // Look for standard fields we care about
                    const standardFields = [
                      { key: 'NAME', fieldName: 'NAME' },
                      { key: 'LAST NAMER', fieldName: 'LAST NAME' },
                      { key: 'LAST NAME', fieldName: 'LAST NAME' },
                      { key: 'FIRST NAME', fieldName: 'FIRST NAME' },
                      { key: 'JERSEY NUMBER', fieldName: 'JERSEY NUMBER' },
                      { key: 'JERSEY', fieldName: 'JERSEY NUMBER' },
                      { key: 'TEAM', fieldName: 'TEAM' },
                      { key: 'TEAM NAME', fieldName: 'TEAM' }
                    ];
                    
                    // Add fields that were explicitly defined in placeholder_data
                    standardFields.forEach(field => {
                      // Look in each category of the placeholder_data
                      Object.entries(templatePlaceholders).forEach(([category, fields]) => {
                        console.log(`Checking category '${category}' for field '${field.key}'`, fields);
                        
                        if (fields[field.key]) {
                          // Found a standard field
                          templateFields.push({
                            name: field.fieldName,
                            originalName: field.key,
                            category: category,
                            value: fields[field.key],
                            type: 'text',
                            template: template.name
                          });
                          console.log(`Found field '${field.key}' in category '${category}'`);
                        }
                      });
                    });
                  }
                  
                  console.log(`Loaded placeholders from template ${template.id} (${template.name})`);
                } catch (e) {
                  console.error(`Error parsing placeholder data for template ${template.id}:`, e);
                }
              } else {
                console.log(`Template ${template.id} has no placeholder_data`);
              }
            });
            
            // Remove duplicates by field name
            const uniqueFields = {};
            templateFields.forEach(field => {
              // Use the field name as the key to avoid duplicates
              uniqueFields[field.name] = field;
            });
            
            templateFields = Object.values(uniqueFields);
            console.log('Final extracted template fields:', templateFields);
          }
        } catch (e) {
          console.error('Error fetching template data:', e);
        }
      } else {
        console.log('No templates associated with this event');
      }
      
      // Pre-populate formData with template fields
      const initialFormData = {};
      
      templateFields.forEach(field => {
        // Convert field name to a valid form field key
        const fieldKey = field.name.replace(/\s+/g, '_').toLowerCase();
        
        // Set initial value
        initialFormData[fieldKey] = field.defaultContent || '';
        
        // Auto-fill certain fields if possible
        if (field.name === 'JERSEY NUMBER' && profileData.jersey_number) {
          initialFormData[fieldKey] = profileData.jersey_number;
        } else if (field.name === 'TEAM' && profileData.team_name) {
          initialFormData[fieldKey] = profileData.team_name;
        } else if (field.name === 'LAST NAME') {
          initialFormData[fieldKey] = '';  // Will be filled in the athlete section
        } else if (field.name === 'FIRST NAME') {
          initialFormData[fieldKey] = '';  // Will be filled in the athlete section
        } else if (field.name === 'NAME') {
          initialFormData[fieldKey] = '';  // Will be filled in the athlete section
        }
      });
      
      console.log('Initial form data from template fields:', initialFormData);
      
      // Add the template fields to the form data
      setFormData(prev => ({
        ...prev,
        ...initialFormData,
        athlete_first_name: '',
        athlete_last_name: '',
      }));
      
      // Store the template fields in the event object
      setEvent({ 
        ...data, 
        tags: parsedTags, 
        templateFields: templateFields,
        placeholders: placeholders 
      });
    } catch (error) {
      console.error('Error fetching event:', error);
      toast({
        title: 'Error',
        description: 'Failed to load event details',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      // Create an account without requiring email verification
      const { data, error } = await supabase.auth.signUp({
        email,
        password: Math.random().toString(36).slice(-8), // Generate a random password
      });
      
      if (error) throw error;
      
      // Set the user and move directly to profile step
      setCurrentUser(data.user);
      setProfileData(prev => ({
        ...prev,
        email: email
      }));
      setSignupProgress(50);
      
      toast({
        title: 'Success',
        description: 'Account created successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error signing up with email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create account',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAuthLoading(false);
    }
  };

  // Helper function to format phone number for display
  const formatPhoneForDisplay = (phoneNumber) => {
    // Remove any non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Don't try to format if we don't have enough digits
    if (digits.length < 3) return digits;
    
    if (digits.length < 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Enhance phone input handling with stricter formatting
  const handlePhoneInput = (e) => {
    // Get value without non-numeric characters
    const rawValue = e.target.value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const digits = rawValue.slice(0, 10);
    
    // Don't store the +1 prefix in the state, just the digits
    setPhone(digits);
  };

  // Handle phone sign-in for read-only database
  const handlePhoneSignIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      // Make sure phone is properly formatted
      if (!phone || phone.replace(/\D/g, '').length < 10) {
        throw new Error('Please enter a valid 10-digit phone number');
      }
      
      // Get properly formatted digits
      const digits = phone.replace(/\D/g, '').slice(-10);
      const formattedPhone = `+1${digits}`;
      
      console.log('Sending verification code to:', formattedPhone);
      
      // Pure authentication - ONLY sign in, no metadata that would trigger DB writes
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone
      });
      
      if (error) throw error;
      
      // Store formatted phone for verification
      setPhone(formattedPhone);
      setSignupProgress(30);
      setAuthStep('otp');
      
      toast({
        title: 'Verification Code Sent',
        description: `Check your phone (${formatPhoneForDisplay(formattedPhone)}) for the verification code`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      
      if (error.message.includes('Database error')) {
        toast({
          title: 'Note',
          description: 'Our database is currently in read-only mode, but verification should still work',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to send verification code',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle OTP verification for read-only database
  const handleOtpVerification = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (!otp || otp.length !== 6) {
        throw new Error('Please enter a valid 6-digit verification code');
      }
      
      console.log('Verifying code for:', phone);
      
      // Verify OTP - pure authentication
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token: otp,
        type: 'sms',
      });
      
      if (error) throw error;
      
      console.log('Authentication successful:', data);
      
      // Set current user
      setCurrentUser(data.user);
      
      // Set basic profile data with phone
      setProfileData(prev => ({
        ...prev,
        phone: phone
      }));
      
      // Skip profile creation since the database is read-only
      // Just show a message and move to the final step
      toast({
        title: 'Success',
        description: 'Phone verified successfully! Database is in read-only mode so profile updates are disabled.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setSignupProgress(100);
      
      // Skip to the final step
      setAuthStep('complete');
      
    } catch (error) {
      console.error('Error verifying code:', error);
      
      if (error.message.includes('Invalid one-time password')) {
        toast({
          title: 'Invalid Code',
          description: 'The verification code you entered is invalid or expired. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to verify code',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      // Make sure we have email
      if (!profileData.email) {
        throw new Error('Email address is required');
      }
      
      let userId = null;
      
      // If not authenticated, create an account
      if (!currentUser) {
        const { data, error } = await supabase.auth.signUp({
          email: profileData.email,
          password: Math.random().toString(36).slice(-8), // Generate a random password
          options: {
            data: {
              first_name: profileData.first_name,
              last_name: profileData.last_name,
              email: profileData.email,
              phone: profileData.phone
            }
          }
        });
        
        if (error) throw error;
        
        // Make sure we have a user
        if (!data?.user?.id) {
          throw new Error('Failed to create user account');
        }
        
        // Set the current user and store the ID
        setCurrentUser(data.user);
        userId = data.user.id;
      } else {
        // Use existing user ID
        userId = currentUser.id;
      }
      
      // Prepare phone number for storage
      let formattedPhone = null;
      if (profileData.phone) {
        formattedPhone = profileData.phone.startsWith('+') 
          ? profileData.phone 
          : profileData.phone.startsWith('1')
            ? `+${profileData.phone}`
            : `+1${profileData.phone.replace(/\D/g, '')}`;
      }
      
      // Format terms accepted data
      const termsAccepted = !!profileData.terms_accepted;
      const termsAcceptedAt = termsAccepted ? new Date().toISOString() : null;
      const termsVersion = termsAccepted ? '1.0' : null;
      
      // Get IP address for terms acceptance
      let termsAcceptedIp = null;
      if (termsAccepted) {
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          if (ipData.ip) {
            termsAcceptedIp = ipData.ip;
          }
        } catch (err) {
          console.error('Could not retrieve IP address:', err);
        }
      }
      
      // Try all three methods to update or create app_users entry
      
      // Method 1: Use RPC function (most reliable)
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('create_event_signup_user_v2', {
          user_id: userId,
          user_email: profileData.email,
          user_first_name: profileData.first_name,
          user_last_name: profileData.last_name,
          user_phone: formattedPhone,
          user_event_id: eventId,
          terms_accepted: termsAccepted,
          terms_version: termsVersion,
          terms_accepted_at: termsAcceptedAt,
          terms_accepted_ip: termsAcceptedIp
        });
        
        if (rpcError) {
          console.error('RPC error:', rpcError);
          throw rpcError;
        }
      } catch (rpcAttemptError) {
        console.error('RPC attempt failed, falling back to direct methods:', rpcAttemptError);
        
        // Method 2: Try direct insert/update
        try {
          console.log('Falling back to direct insert/update');
          const userData = {
            id: userId,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
            phone: formattedPhone,
            user_type: 'customer',
            event_id: eventId,
            terms_accepted: termsAccepted,
            terms_version: termsVersion,
            terms_accepted_at: termsAcceptedAt,
            terms_accepted_ip: termsAcceptedIp
          };
          
          // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('app_users')
        .select('id')
            .eq('id', userId)
        .single();
      
      if (existingUser) {
        // Update existing user
            const { error: updateError } = await supabase
          .from('app_users')
              .update(userData)
              .eq('id', userId);
              
            if (updateError) throw updateError;
      } else {
        // Insert new user
            const { error: insertError } = await supabase
          .from('app_users')
              .insert(userData);
              
            if (insertError) throw insertError;
          }
        } catch (directError) {
          console.error('Direct operation failed, using execute_sql as last resort:', directError);
          
          // Method 3: Last resort - direct SQL
          try {
            const sqlQuery = `
              INSERT INTO app_users (id, first_name, last_name, email, phone, user_type, event_id, terms_accepted)
              VALUES ('${userId}', '${profileData.first_name}', '${profileData.last_name}', 
                      '${profileData.email}', ${formattedPhone ? `'${formattedPhone}'` : 'NULL'}, 
                      'customer', ${eventId ? `'${eventId}'` : 'NULL'}, ${termsAccepted})
              ON CONFLICT (id) DO UPDATE SET
                first_name = EXCLUDED.first_name,
                last_name = EXCLUDED.last_name,
                email = EXCLUDED.email,
                phone = EXCLUDED.phone,
                user_type = EXCLUDED.user_type,
                event_id = COALESCE(${eventId ? `'${eventId}'` : 'NULL'}, app_users.event_id),
                terms_accepted = EXCLUDED.terms_accepted
            `;
            
            const { error: sqlError } = await supabase.rpc('execute_sql', { 
              sql_statement: sqlQuery
            });
            
            if (sqlError) throw sqlError;
          } catch (sqlError) {
            console.error('SQL execution failed, trying create_simple_user:', sqlError);
            
            // Final fallback: try the simplest possible function
            try {
              const { data: simpleData, error: simpleError } = await supabase.rpc('create_simple_user', {
                p_user_id: userId,
                p_first_name: profileData.first_name,
                p_last_name: profileData.last_name,
                p_email: profileData.email,
                p_event_id: eventId
              });
              
              if (simpleError) throw simpleError;
            } catch (finalError) {
              console.error('All database methods failed:', finalError);
              // Display error but continue with registration
              toast({
                title: 'Database Update Issue',
                description: `Profile saved but some data may be incomplete. Will try again at checkout.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }
      }
      
      console.log('Profile updated successfully');
      setSignupProgress(100);
      
      // Update athlete information in form data
      setFormData(prev => ({
        ...prev,
        athlete_first_name: profileData.first_name || '',
        athlete_last_name: profileData.last_name || '',
        name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim()
      }));
      
      toast({
        title: 'Success',
        description: 'Profile updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // If this is not for an event, go to dashboard
      if (!eventId) {
        console.log('This is not for an event, navigating to dashboard');
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleInputChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    
    try {
      // Get the current user ID or create an account if none exists
      const { data: { session } } = await supabase.auth.getSession();
      let userId;
      
      // Check if the email in the form is different from the authenticated user
      const formEmailIsDifferent = session?.user?.email && 
        profileData.email && 
        session.user.email.toLowerCase() !== profileData.email.toLowerCase();
      
      if (formEmailIsDifferent) {
        console.log('Email in form differs from authenticated user. Creating new account...');
        // Create a new user account with the form email
        if (!profileData.email) {
          throw new Error('Email address is required to complete registration');
        }
        
        // Create a new account
        const { data, error } = await supabase.auth.signUp({
          email: profileData.email,
          password: Math.random().toString(36).slice(-8), // Generate a random password
          options: {
            data: {
              first_name: profileData.first_name,
              last_name: profileData.last_name,
              email: profileData.email,
              phone: profileData.phone
            }
          }
        });
        
        if (error) throw error;
        
        // Make sure we have a user
        if (!data?.user?.id) {
          throw new Error('Failed to create user account');
        }
        
        console.log('Created new user account:', data.user.id);
        setCurrentUser(data.user);
        userId = data.user.id;
      } else if (!session?.user?.id) {
        console.log('No authenticated user found, creating a new account...');
        // Create a new user account if the user is not signed in
        if (!profileData.email) {
          throw new Error('Email address is required to complete registration');
        }
        
        // Create a new account
        const { data, error } = await supabase.auth.signUp({
          email: profileData.email,
          password: Math.random().toString(36).slice(-8), // Generate a random password
          options: {
            data: {
              first_name: profileData.first_name,
              last_name: profileData.last_name,
              email: profileData.email,
              phone: profileData.phone
            }
          }
        });
        
        if (error) throw error;
        
        // Make sure we have a user
        if (!data?.user?.id) {
          throw new Error('Failed to create user account');
        }
        
        console.log('Created new user account:', data.user.id);
        setCurrentUser(data.user);
        userId = data.user.id;
      } else {
        userId = session.user.id;
      }
      
      console.log('Proceeding with user ID:', userId);
      
      // Prepare phone number for storage
      let formattedPhone = null;
      if (profileData.phone) {
        formattedPhone = profileData.phone.startsWith('+') 
          ? profileData.phone 
          : profileData.phone.startsWith('1')
            ? `+${profileData.phone}`
            : `+1${profileData.phone.replace(/\D/g, '')}`;
      }
      
      // Format terms accepted data
      const termsAccepted = !!profileData.terms_accepted;
      const termsAcceptedAt = termsAccepted ? new Date().toISOString() : null;
      const termsVersion = termsAccepted ? '1.0' : null;
      
      // Get IP address for terms acceptance
      let termsAcceptedIp = null;
      if (termsAccepted) {
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          if (ipData.ip) {
            termsAcceptedIp = ipData.ip;
          }
        } catch (err) {
          console.error('Could not retrieve IP address:', err);
        }
      }
      
      // Try all three methods to update or create app_users entry
      
      // Method 1: Use RPC function (most reliable)
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('create_event_signup_user_v2', {
          user_id: userId,
          user_email: profileData.email,
          user_first_name: profileData.first_name,
          user_last_name: profileData.last_name,
          user_phone: formattedPhone,
          user_event_id: eventId,
          terms_accepted: termsAccepted,
          terms_version: termsVersion,
          terms_accepted_at: termsAcceptedAt,
          terms_accepted_ip: termsAcceptedIp
        });
        
        if (rpcError) {
          console.error('RPC error:', rpcError);
          throw rpcError;
        }
      } catch (rpcAttemptError) {
        console.error('RPC attempt failed, falling back to direct methods:', rpcAttemptError);
        
        // Method 2: Try direct insert/update
        try {
          console.log('Falling back to direct insert/update');
          const userData = {
            id: userId,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
            phone: formattedPhone,
            user_type: 'customer',
            event_id: eventId,
            terms_accepted: termsAccepted,
            terms_version: termsVersion,
            terms_accepted_at: termsAcceptedAt,
            terms_accepted_ip: termsAcceptedIp
          };
          
          // Check if user already exists
          const { data: existingUser, error: checkError } = await supabase
            .from('app_users')
            .select('id')
            .eq('id', userId)
            .single();
            
          if (existingUser) {
            // Update existing user
            const { error: updateError } = await supabase
              .from('app_users')
              .update(userData)
              .eq('id', userId);
              
            if (updateError) throw updateError;
          } else {
            // Insert new user
            const { error: insertError } = await supabase
              .from('app_users')
              .insert(userData);
              
            if (insertError) throw insertError;
          }
        } catch (directError) {
          console.error('Direct operation failed, using execute_sql as last resort:', directError);
          
          // Method 3: Last resort - direct SQL
          try {
            const sqlQuery = `
              INSERT INTO app_users (id, first_name, last_name, email, phone, user_type, event_id, terms_accepted)
              VALUES ('${userId}', '${profileData.first_name}', '${profileData.last_name}', 
                      '${profileData.email}', ${formattedPhone ? `'${formattedPhone}'` : 'NULL'}, 
                      'customer', ${eventId ? `'${eventId}'` : 'NULL'}, ${termsAccepted})
              ON CONFLICT (id) DO UPDATE SET
                first_name = EXCLUDED.first_name,
                last_name = EXCLUDED.last_name,
                email = EXCLUDED.email,
                phone = EXCLUDED.phone,
                user_type = EXCLUDED.user_type,
                event_id = COALESCE(${eventId ? `'${eventId}'` : 'NULL'}, app_users.event_id),
                terms_accepted = EXCLUDED.terms_accepted
            `;
            
            const { error: sqlError } = await supabase.rpc('execute_sql', { 
              sql_statement: sqlQuery
            });
            
            if (sqlError) throw sqlError;
          } catch (sqlError) {
            console.error('SQL execution failed, trying create_simple_user:', sqlError);
            
            // Final fallback: try the simplest possible function
            try {
              const { data: simpleData, error: simpleError } = await supabase.rpc('create_simple_user', {
                p_user_id: userId,
                p_first_name: profileData.first_name,
                p_last_name: profileData.last_name,
                p_email: profileData.email,
                p_event_id: eventId
              });
              
              if (simpleError) throw simpleError;
            } catch (finalError) {
              console.error('All database methods failed:', finalError);
              // Display error but continue with registration
              toast({
                title: 'Database Update Issue',
                description: `Profile saved but some data may be incomplete. Will try again at checkout.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }
      }
      
      // Check if the user has already registered for this event
      const { data: existingOrder, error: checkOrderError } = await supabase
        .from('event_orders')
        .select('id, status')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .single();
        
      if (checkOrderError && checkOrderError.code !== 'PGRST116') { // PGRST116 is the "not found" error
        throw checkOrderError;
      }
      
      // Update form data with information from profileData
      const completeFormData = {
        ...formData,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone,
        athlete_first_name: formData.athlete_first_name || '',
        athlete_last_name: formData.athlete_last_name || ''
      };
      
      let result;
      
      if (existingOrder) {
        // User already has an order for this event, update it instead
        result = await supabase
          .from('event_orders')
          .update({
            answers: completeFormData,
            status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('id', existingOrder.id);
          
        if (result.error) throw result.error;
        
        toast({
          title: 'Registration Updated',
          description: 'Your event registration has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new registration
        result = await supabase
        .from('event_orders')
        .insert({
          event_id: eventId,
            user_id: userId,
            answers: completeFormData,
          status: 'pending'
        });

        if (result.error) throw result.error;

        toast({
          title: 'Success',
          description: 'Successfully signed up for the event',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Send confirmation email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-confirmation-email', {
          body: {
            email: profileData.email,
            firstName: profileData.first_name,
            eventName: event.name,
            eventDate: new Date(event.start_date).toLocaleDateString(),
            eventLocation: `${event.city}, ${event.state}`
          }
        });
        
        if (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't throw error, just log it and continue
        } else {
          console.log('Confirmation email sent successfully');
        }
      } catch (emailError) {
        console.error('Error invoking email function:', emailError);
        // Don't throw error, just log it and continue
      }
      
      // Navigate to dashboard
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Specific handling for duplicate key violation
      if (error.code === '23505') {
        toast({
          title: 'Already Registered',
          description: 'You have already registered for this event',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        // Still navigate to dashboard since they're already registered
        navigate('/dashboard');
        return;
      }
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit form',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Container centerContent py={10}>
        <Spinner size="xl" />
      </Container>
    );
  }

  if (!eventId) {
    return (
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>Available Events</Heading>
            <Text color="gray.600">Select an event to sign up</Text>
          </Box>

          {events.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text fontSize="xl" color="gray.600">
                No events are currently available for signup.
              </Text>
              <Text mt={2} color="gray.500">
                Please check back later or contact the event organizer for more information.
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {events.map((event) => (
                <Card 
                  key={event.id} 
                  cursor="pointer" 
                  onClick={() => navigate(`/signup/${event.id}`)}
                  overflow="hidden"
                  borderRadius="lg"
                  boxShadow="md"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  position="relative"
                >
                  {/* Background image with overlay */}
                  <Box 
                    position="absolute" 
                    top="0" 
                    left="0" 
                    right="0" 
                    height="100%" 
                    backgroundImage={event.event_image ? `url(${event.event_image})` : 'linear-gradient(to right, #4299e1, #3182ce)'}
                    backgroundSize="cover"
                    backgroundPosition="center"
                    filter="brightness(0.4)"
                    zIndex="0"
                  />
                  
                  {/* Event logo if available */}
                  {event.event_image && (
                    <Flex 
                      position="absolute" 
                      top="4" 
                      right="4" 
                      zIndex="1"
                      justifyContent="center"
                      alignItems="center"
                      bg="white"
                      borderRadius="full"
                      p="2"
                      boxSize="60px"
                      overflow="hidden"
                    >
                      <Image 
                        src={event.event_image} 
                        alt={event.name}
                        objectFit="contain"
                        boxSize="100%"
                      />
                    </Flex>
                  )}
                  
                  <CardHeader position="relative" zIndex="1">
                    <Heading size="md" color="white">{event.name}</Heading>
                  </CardHeader>
                  
                  <CardBody position="relative" zIndex="1" color="white">
                    {event.description && (
                      <Text mb={4} noOfLines={2}>{event.description}</Text>
                    )}
                    
                    <HStack mt={2} spacing={4}>
                      <HStack>
                        <Icon as={CalendarIcon} color="blue.200" />
                        <Text fontSize="sm">
                          {new Date(event.start_date).toLocaleDateString()}
                        </Text>
                      </HStack>
                      {event.city && event.state && (
                        <HStack>
                          <Icon as={InfoIcon} color="blue.200" />
                          <Text fontSize="sm">
                            {event.city}, {event.state}
                          </Text>
                        </HStack>
                      )}
                    </HStack>
                    
                    {/* Event tags */}
                    {event.tags && Array.isArray(event.tags) && event.tags.length > 0 && (
                      <Flex mt={3} wrap="wrap" gap={2}>
                        {event.tags.slice(0, 3).map((tag, index) => (
                          <Badge 
                            key={index} 
                            colorScheme="blue" 
                            variant="solid" 
                            fontSize="xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                        {event.tags.length > 3 && (
                          <Badge colorScheme="gray" variant="solid" fontSize="xs">
                            +{event.tags.length - 3}
                          </Badge>
                        )}
                      </Flex>
                    )}
                  </CardBody>
                  
                  <CardFooter 
                    position="relative" 
                    zIndex="1" 
                    borderTopWidth="1px" 
                    borderColor="whiteAlpha.300"
                    py={3}
                  >
                    <Flex width="full" direction="column" gap={2}>
                      <Button 
                        colorScheme="blue" 
                        width="full"
                        bg="whiteAlpha.900" 
                        color="blue.800"
                        _hover={{ bg: "white", color: "blue.900" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/signup/${event.id}`);
                        }}
                      >
                        Sign Up
                      </Button>
                      
                      {event.templates && (
                        <Button
                          variant="ghost" 
                          size="sm"
                          color="white"
                          leftIcon={<ViewIcon />}
                          rightIcon={
                            event.templates.split(',').filter(id => id).length > 0 ? (
                              <Code 
                                colorScheme="blue" 
                                bg="blue.700"
                                color="white"
                                children={event.templates.split(',').filter(id => id).length} 
                                px={2}
                                py={0}
                                borderRadius="full"
                                fontSize="xs"
                              />
                            ) : null
                          }
                          _hover={{ bg: "whiteAlpha.200" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/events/${event.id}/templates`);
                          }}
                        >
                          Browse Templates
                        </Button>
                      )}
                    </Flex>
                  </CardFooter>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container centerContent py={10}>
        <Text>Event not found</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero section with event background and logo */}
      <Box
        position="relative"
        height="200px"
        backgroundImage={event.event_image ? `url(${event.event_image})` : 'linear-gradient(to right, #4299e1, #3182ce)'}
        backgroundSize="cover"
        backgroundPosition="center"
        mb={6}
      >
        {/* Overlay for better text visibility */}
        <Box 
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
        />
        
        {/* Event logo and title */}
        <Container maxW="container.md" position="relative" height="100%">
          <Flex 
            height="100%" 
            alignItems="center"
            position="relative"
          >
            {event.event_image && (
              <Box
                bg="white"
                borderRadius="full"
                p="3"
                boxSize="100px"
                mr={4}
                boxShadow="md"
              >
                <Image
                  src={event.event_image}
                  alt={event.name}
                  objectFit="contain"
                  boxSize="100%"
                />
              </Box>
            )}
            
            <Box>
              <Heading color="white" size="lg" mb={2}>{event.name}</Heading>
              <HStack spacing={5} color="white" opacity={0.9}>
                <HStack>
                  <Icon as={CalendarIcon} />
                  <Text>{new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}</Text>
                </HStack>
                <HStack>
                  <Icon as={InfoIcon} />
                  <Text>{event.city}, {event.state}</Text>
                </HStack>
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>
      
      <Container maxW="container.lg" py={6}>
        {/* Switch to a grid layout with main content on left and details card on right */}
        <Flex flexDirection={{ base: "column", md: "row" }} gap={6}>
          {/* Main content column (left side) - increase flex ratio */}
          <Box flex={{ base: "1", md: "8" }}>
        <VStack spacing={8} align="stretch">
          {signupProgress > 0 && (
            <Box>
              <Text mb={1} fontWeight="medium">Registration Progress</Text>
              <Progress value={signupProgress} size="sm" colorScheme="blue" borderRadius="md" />
            </Box>
          )}

              {/* Profile Creation Form - Direct Entry */}
              <Box>
                <Box bg="white" p={8} borderRadius="md" boxShadow="md">
              <Heading size="md" mb={6} color="blue.700" textTransform="uppercase" letterSpacing="wide">
                    SIGN UP FOR THIS {event.event_type ? (
                      <Tooltip
                        hasArrow
                        label={
                          <Box>
                            This word dynamically pulls from the 'event type' column in the database that admins can edit. Ie. if the event type is 'tournament', it will display 'TOURNAMENT' here:{' '}
                            <Link 
                              href={`/dashboard/events/${eventId}/edit`}
                              color="white"
                              textDecoration="underline"
                              fontWeight="bold"
                              isExternal
                            >
                              dashboard/events/{eventId}/edit
                            </Link>
                          </Box>
                        }
                        bg="blue.600"
                        color="white"
                        placement="top"
                        openDelay={300}
                        maxW="400px"
                        p={3}
                        borderRadius="md"
                        fontSize="sm"
                        boxShadow="lg"
                        isDisabled={hideTooltips}
                      >
                        <Box as="span" borderBottom="1px dashed" borderColor="blue.400" display="inline-block" position="relative">
                          {event.event_type.toUpperCase()} <InfoIcon color="orange.400" boxSize={3} ml={1} style={{ visibility: hideTooltips ? 'hidden' : 'visible' }} />
                        </Box>
                      </Tooltip>
                    ) : 'EVENT'}
                  </Heading>
                  
                  {event.description && (
                    <Box mb={6} p={4} bg="gray.50" borderRadius="md">
                      <Text color="gray.700">
                        {event.description}{' '}
                        <Tooltip
                          hasArrow
                          label={
                            <Box>
                              Admins can edit this Event Description from the event management/creation process
                            </Box>
                          }
                          bg="blue.600"
                          color="white"
                          placement="top"
                          openDelay={300}
                          maxW="400px"
                          p={3}
                          borderRadius="md"
                          fontSize="sm"
                          boxShadow="lg"
                          isDisabled={hideTooltips}
                        >
                          <InfoIcon color="orange.400" boxSize={3} ml={1} style={{ visibility: hideTooltips ? 'hidden' : 'visible' }} />
                        </Tooltip>
                      </Text>
                    </Box>
                  )}
                
                <form onSubmit={handleProfileUpdate}>
                  <VStack spacing={6} align="stretch">
                      <Heading size="sm" color="blue.700" mb={2}>PARENT/GUARDIAN INFO</Heading>
                      
                      <HStack spacing={8}>
                      <FormControl isRequired>
                        <FormLabel fontWeight="bold" color="gray.700">FIRST NAME *</FormLabel>
                        <Input
                          name="first_name"
                          value={profileData.first_name}
                          onChange={handleProfileInputChange}
                          placeholder="Your first name"
                          size="md"
                          bg="white"
                          borderColor="gray.300"
                        />
                      </FormControl>
                      
                      <FormControl isRequired>
                        <FormLabel fontWeight="bold" color="gray.700">LAST NAME *</FormLabel>
                        <Input
                          name="last_name"
                          value={profileData.last_name}
                          onChange={handleProfileInputChange}
                          placeholder="Your last name"
                          size="md"
                          bg="white"
                          borderColor="gray.300"
                        />
                      </FormControl>
                    </HStack>
                    
                      <FormControl isRequired>
                        <FormLabel fontWeight="bold" color="gray.700">EMAIL ADDRESS *</FormLabel>
                      <Input
                        name="email"
                          value={profileData.email || ''}
                        onChange={handleProfileInputChange}
                        placeholder="your.email@example.com"
                        type="email"
                        bg="white"
                        borderColor="gray.300"
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel fontWeight="bold" color="gray.700">PHONE NUMBER</FormLabel>
                      <InputGroup>
                        <InputLeftAddon children="+1" bg="gray.100" color="gray.600" />
                        <Input
                          name="phone"
                          value={phone ? formatPhoneForDisplay(phone) : ''}
                          onChange={handlePhoneInput}
                          placeholder="123-456-7890"
                          bg="white"
                          borderColor="gray.300"
                        />
                      </InputGroup>
                    </FormControl>

                    <Heading size="sm" color="blue.700" mb={2}>ATHLETE INFO</Heading>
                    
                    <HStack spacing={8}>
                      <FormControl>
                        <FormLabel fontWeight="bold" color="gray.700">ATHLETE FIRST NAME</FormLabel>
                        <Input
                          name="athlete_first_name"
                          value={formData.athlete_first_name || ''}
                          onChange={(e) => handleInputChange('athlete_first_name', e.target.value)}
                          placeholder="Athlete's first name"
                          size="md"
                          bg="white"
                          borderColor="gray.300"
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontWeight="bold" color="gray.700">ATHLETE LAST NAME</FormLabel>
                        <Input
                          name="athlete_last_name"
                          value={formData.athlete_last_name || ''}
                          onChange={(e) => handleInputChange('athlete_last_name', e.target.value)}
                          placeholder="Athlete's last name"
                          size="md"
                          bg="white"
                          borderColor="gray.300"
                        />
                      </FormControl>
                    </HStack>
                    </VStack>
                  </form>
                </Box>
              </Box>

              {/* Event Registration Form */}
              {console.log('Current auth step before rendering registration form:', authStep)}
              {authStep === 'complete' ? (
                <Box bg="white" p={8} borderRadius="md" boxShadow="md">
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6} align="stretch">
                      {/* Success registration alert */}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                          After registration, a confirmation email will be sent to your email address.
                        </Text>
                      </Alert>
                      
                      {/* Display Template Fields - HIDDEN but still functional */}
                      {/* We're keeping the formData state but not displaying the UI */}
                      {event.templateFields && event.templateFields.length > 0 && (
                        <Box display="none">
                          {event.templateFields.map((field) => {
                            const fieldKey = field.name.replace(/\s+/g, '_').toLowerCase();
                            
                            if (field.name === 'FIRST NAME') {
                              return (
                                <Input
                                  key={fieldKey}
                                  type="hidden"
                                  value={formData.athlete_first_name || ''}
                                  onChange={(e) => handleInputChange('athlete_first_name', e.target.value)}
                                />
                              );
                            } else if (field.name === 'LAST NAME') {
                              return (
                                <Input
                                  key={fieldKey}
                                  type="hidden"
                                  value={formData.athlete_last_name || ''}
                                  onChange={(e) => handleInputChange('athlete_last_name', e.target.value)}
                                />
                              );
                            } else if (field.name === 'NAME') {
                              return (
                                <Input
                                  key={fieldKey}
                                  type="hidden"
                                  value={
                                    formData.athlete_first_name && formData.athlete_last_name
                                      ? `${formData.athlete_first_name} ${formData.athlete_last_name}`
                                      : formData[fieldKey] || ''
                                  }
                                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                />
                              );
                            } else if (field.name === 'JERSEY NUMBER') {
                              return (
                                <Input
                                  key={fieldKey}
                                  type="hidden"
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                />
                              );
                            } else {
                              return (
                                <Input
                                  key={fieldKey}
                                  type="hidden"
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                />
                              );
                            }
                          })}
                        </Box>
                      )}
                      
                      {/* Standard Event Questions */}
                      {event.questions && Array.isArray(event.questions) && event.questions.length > 0 ? (
                        <>
                          <Heading size="sm" color="blue.700" mb={4}>
                            EVENT QUESTIONS
              </Heading>
              
                          {event.questions.map((question) => (
                      <FormControl key={question.id} isRequired={question.required}>
                        <FormLabel fontWeight="bold" color="gray.700">
                          {question.text}
                          {question.required && (
                            <Text as="span" color="red.500" ml={1}>*</Text>
                          )}
                        </FormLabel>

                        {question.type === 'text' && (
                          <Input
                            value={formData[question.id] || ''}
                            onChange={(e) => handleInputChange(question.id, e.target.value)}
                            size="md"
                            bg="white"
                            borderColor="gray.300"
                          />
                        )}

                        {question.type === 'radio' && (
                          <RadioGroup
                            value={formData[question.id] || ''}
                            onChange={(value) => handleInputChange(question.id, value)}
                          >
                            <Stack spacing={2}>
                                    {question.options && question.options.length > 0 ? (
                                      question.options.map((option) => (
                                <Radio 
                                  key={option} 
                                  value={option}
                                  colorScheme="blue"
                                  size="md"
                                >
                                  {option}
                                </Radio>
                                      ))
                                    ) : (
                                      <Text color="gray.500">No options available</Text>
                                    )}
                            </Stack>
                          </RadioGroup>
                        )}

                        {question.type === 'checkbox' && (
                          <CheckboxGroup
                            value={formData[question.id] || []}
                            onChange={(value) => handleInputChange(question.id, value)}
                          >
                            <Stack spacing={2}>
                                    {question.options && question.options.length > 0 ? (
                                      question.options.map((option) => (
                                <Checkbox 
                                  key={option} 
                                  value={option}
                                  colorScheme="blue"
                                  size="md"
                                >
                                  {option}
                                </Checkbox>
                                      ))
                                    ) : (
                                      <Text color="gray.500">No options available</Text>
                                    )}
                            </Stack>
                          </CheckboxGroup>
                        )}
                      </FormControl>
                          ))}
                        </>
                  ) : (
                        !event.templateFields || event.templateFields.length === 0 ? (
                    <Box textAlign="center" py={8}>
                            <Text color="gray.600" mb={4}>No additional information required for this event.</Text>
                      <Text color="gray.600">Click the button below to complete your registration.</Text>
                    </Box>
                        ) : null
                  )}

                  <Divider my={2} />
                  
                      <Box bg="gray.50" p={4} borderRadius="md" fontSize="sm" color="gray.600">
                        <Text fontWeight="medium" mb={2}>
                          NOTE: PROVIDING YOUR CONTACT INFORMATION ALLOWS US TO SEND YOU IMAGES AND NOTIFY YOU ABOUT FUTURE UPDATES.
                        </Text>
                        <HStack mt={4}>
                      <Checkbox 
                        isRequired 
                        size="md"
                        colorScheme="blue"
                            name="terms_accepted"
                            onChange={(e) => handleProfileInputChange({ 
                              target: { 
                                name: 'terms_accepted', 
                                value: e.target.checked 
                              } 
                            })}
                          />
                          <Text fontSize="sm">
                            I ACCEPT THE <Text as="span" color="blue.500" fontWeight="bold">TERMS OF SERVICE</Text> AND <Text as="span" color="blue.500" fontWeight="bold">PRIVACY POLICY</Text>
                      </Text>
                    </HStack>
                  </Box>

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    py={6}
                    width="full"
                    mt={4}
                  >
                    Complete Registration
                  </Button>
                </VStack>
              </form>
            </Box>
              ) : null}
        </VStack>
          </Box>
          
          {/* Details card column (right side) */}
          <Box flex={{ base: "1", md: "3" }} display={{ base: "none", md: "block" }}>
            {/* EventDetailsCard */}
            {event && (
              <Box
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                borderColor="gray.200"
                boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                position="relative"
                transition="all 0.3s"
                sx={{ position: "sticky", top: "20px" }}
                _hover={{ boxShadow: "0 6px 8px rgba(0, 0, 0, 0.1)" }}
              >
                {/* Collapsible Header */}
                <Flex 
                  p={4} 
                  justifyContent="space-between" 
                  alignItems="center"
                  cursor="pointer"
                  bg="gray.50"
                  onClick={() => setShowLocationDetails(!showLocationDetails)}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={4}>
                    <Icon 
                      as={InfoIcon} 
                      color="blue.500" 
                      boxSize={6} 
                      opacity={0.8}
                      transform={showLocationDetails ? "rotate(0deg)" : "rotate(-15deg)"}
                      transition="transform 0.3s ease"
                    />
                    <Box>
                      <Text fontWeight="bold" fontSize="md">LOCATION & DETAILS</Text>
                      <Text color="gray.500" fontSize="sm">{event.city}, {event.state}</Text>
                    </Box>
                  </HStack>

                  <Icon 
                    as={showLocationDetails ? FaChevronUp : FaChevronDown} 
                    boxSize={4}
                  />
                </Flex>

                {/* Collapsible Details */}
                <Collapse in={showLocationDetails} animateOpacity>
                  <Box p={4} borderTopWidth={showLocationDetails ? "1px" : "0"}>
                    <VStack align="stretch" spacing={3}>
                      <HStack>
                        <Icon as={CalendarIcon} color="blue.500" />
                        <Text fontWeight="medium">
                          {new Date(event.start_date).toLocaleDateString(undefined, {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                          })}
                          {event.start_date !== event.end_date && ` - ${new Date(event.end_date).toLocaleDateString(undefined, {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                          })}`}
                        </Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={TimeIcon} color="blue.500" />
                        <Text fontWeight="medium">
                          {new Date(event.start_date).toLocaleTimeString(undefined, {
                            hour: 'numeric', minute: '2-digit', hour12: true
                          })}
                        </Text>
                      </HStack>
                      
                      <HStack alignItems="flex-start">
                        <Icon as={FaMapMarkerAlt} color="blue.500" mt={1} />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">{event.city}, {event.state}</Text>
                          <Text fontSize="sm">
                            {event.address1}
                            {event.address2 ? `, ${event.address2}` : ''}, {event.city}, {event.state} {event.zip}
                          </Text>
                        </VStack>
                      </HStack>
                      
                      <Badge alignSelf="flex-start" colorScheme={
                        event.status === 'active' ? 'green' : 
                        event.status === 'draft' ? 'yellow' : 
                        event.status === 'completed' ? 'gray' : 'red'
                      }>
                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                      </Badge>

                      <Button 
                        leftIcon={<FaExternalLinkAlt />}
                        colorScheme="blue"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          const formattedAddress = `${event.address1}${event.address2 ? ', ' + event.address2 : ''}, ${event.city}, ${event.state} ${event.zip}`;
                          const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`;
                          window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
                        }}
                        mt={2}
                        _hover={{
                          transform: "translateY(-2px)",
                          boxShadow: "lg"
                        }}
                        transition="all 0.2s"
                      >
                        View Map
                      </Button>
                    </VStack>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
} 